import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function AstraZenecaVaccinesPage() {
  useSEO({
    title: 'AstraZeneca Vaccines Strategy | Design For',
    description: 'Strategic consulting for vaccine development and patient engagement. Research-driven strategy for pharmaceutical vaccine programmes.',
    keywords: 'vaccine strategy, pharmaceutical consulting, patient engagement, healthcare innovation, vaccine development',
    canonical: '/work/astrazeneca-vaccines',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for vaccine development and patient engagement',
    description: 'We partnered with AstraZeneca to develop strategic frameworks for vaccine programmes, patient engagement, and healthcare delivery across diverse populations.',
    url: 'https://example.com/work/astrazeneca-vaccines',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - Vaccine strategy',
    keywords: 'vaccine strategy, pharmaceutical consulting, patient engagement, healthcare innovation, vaccine development',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Reimagining how crucial vaccines are brought to market
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Top 10 Pharmaceutical Company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                This pharmaco. wanted to fundamentally rethink how one of its longest standing vaccine brands was brought to market. Rather than incremental optimisation, the ambition was a human-centred strategy that reflected the realities of patients, healthcare professionals, regulators, and governments across global markets.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Leading a global, cross-functional team, we redesigned the go to market approach from the ground up combining deep stakeholder insight with new strategic tools to drive growth, alignment, and adoption at scale.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted 40+ interviews with global and local stakeholders',
                  'Delivered 3 co-design workshops to shape the 2025 strategy',
                  'Created a market archetyping framework to guide country level execution',
                  'Designed 6 future state experience maps across patients, HCPs, regulators, and payers',
                  'Developed 12 market specific personas',
                  'Defined 3 strategic pillars aligned to the 2025 brand plan',
                  '3 strategic recommendations and health economics data that was briefed to the CEO and presented to the UK Prime Minister Rishi Sunak to inform the UK\'s flu vaccine rollout',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/59ca997d5d37a61c379ae60ca4f40e99.jpeg"
                  alt="AstraZeneca global strategy workshop"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/73f441ff9327f96094be9abab290051d.jpg"
                  alt="AstraZeneca vaccine go-to-market strategy framework"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="astrazeneca-vaccines" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
